import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { brl } from "@/api";
// Remove tag interna de fornecedor no formato " [xxx]" ao final ou em
// qualquer posição — não deve aparecer para o cliente.
const stripSupplierTag = (s) => (s ? String(s).replace(/\s*\[[^\[\]]*\]\s*/g, " ").replace(/\s+/g, " ").trim() : "");
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, MapPin, Copy, MessageCircle, Wallet, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";

export default function PublicBalance() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pixOpen, setPixOpen] = useState(false);
  const [pixAmount, setPixAmount] = useState("");
  const [pixMode, setPixMode] = useState("partial"); // partial | total
  const [pixData, setPixData] = useState(null); // { brcode, amount, ... }
  const [busy, setBusy] = useState(false);

  // Garante tema claro (esta página é pública e sempre clara)
  useLayoutEffect(() => {
    const root = document.documentElement;
    const had = root.classList.contains("dark");
    if (had) root.classList.remove("dark");
    return () => { if (had) root.classList.add("dark"); };
  }, []);

  useEffect(() => {
    axios.get(`${API}/public/balance/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Link inválido ou desativado"));
  }, [token]);

  // Pré-preenche valor sugerido se vier ?valor=N na URL
  useEffect(() => {
    const v = searchParams.get("valor");
    if (v && !pixAmount) {
      const num = Number(v);
      if (!isNaN(num) && num > 0) setPixAmount(num.toFixed(2).replace(".", ","));
    }
  }, [searchParams, pixAmount]);

  const debtAmount = useMemo(() => {
    if (!data) return 0;
    return Math.max(0, -Number(data.balance || 0));
  }, [data]);

  const parseCurrencyInput = (v) => {
    if (typeof v !== "string") return Number(v) || 0;
    return Number(v.replace(/\./g, "").replace(",", ".")) || 0;
  };

  const generatePix = async (mode) => {
    if (!data) return;
    let amount = 0;
    if (mode === "total") {
      amount = debtAmount;
      if (amount <= 0) { toast.info("Sem saldo devedor para pagar"); return; }
    } else {
      amount = parseCurrencyInput(pixAmount);
      if (amount <= 0) { toast.error("Informe um valor maior que zero"); return; }
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/balance/${token}/pix`, { amount });
      setPixData({ ...r.data, mode });
      setPixOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível gerar o PIX");
    } finally { setBusy(false); }
  };

  const copyBrcode = async () => {
    if (!pixData?.brcode) return;
    try {
      await navigator.clipboard.writeText(pixData.brcode);
      toast.success("Código PIX copiado");
    } catch {
      toast.error("Não foi possível copiar — selecione e copie manualmente");
    }
  };

  const openWhatsApp = () => {
    if (!data) return;
    const phone = (data.store?.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Loja sem WhatsApp cadastrado"); return; }
    const wa = phone.length >= 11 ? `55${phone}` : phone;
    const isTotal = pixData?.mode === "total";
    const valueText = pixData?.amount ? ` ${brl(pixData.amount)}` : "";
    const msg = isTotal
      ? `Comprovante de pagamento total${valueText}`
      : `Comprovante de pagamento parcial${valueText}`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-md shadow-sm border border-[#E5E5E5] max-w-sm w-full p-6 text-center">
          <div className="text-4xl mb-3">🔒</div>
          <div className="font-display text-lg mb-1">Acesso indisponível</div>
          <div className="text-sm text-neutral-600">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-sm text-neutral-500">Carregando…</div>
      </div>
    );
  }

  const balanceColor = data.balance < 0 ? "text-red-600" : data.balance > 0 ? "text-emerald-600" : "text-neutral-600";

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Cabeçalho da loja */}
        <div className="bg-white rounded-md shadow-sm border border-[#E5E5E5] p-4 flex items-center gap-3">
          {data.store?.logo && (
            <img src={data.store.logo} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0"/>
          )}
          <div className="min-w-0">
            <div className="font-display text-base truncate">{data.store?.name || "Loja"}</div>
            {data.store?.address && (
              <div className="text-[11px] text-neutral-500 flex items-center gap-1 truncate">
                <MapPin size={10}/>{data.store.address}
              </div>
            )}
            {data.store?.phone && (
              <div className="text-[11px] text-neutral-500 flex items-center gap-1">
                <Phone size={10}/>{data.store.phone}
              </div>
            )}
          </div>
        </div>

        {/* Título + cliente + saldo */}
        <div className="bg-white rounded-md shadow-sm border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
            Link de acompanhamento de extrato do saldo
          </div>
          <div className="font-display text-lg" data-testid="public-balance-customer">
            {data.customer?.name || "Cliente"}
          </div>
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">Saldo total</div>
              <div className={`font-display text-3xl ${balanceColor}`} data-testid="public-balance-amount">
                {brl(data.balance)}
              </div>
              {data.balance < 0 && (
                <div className="text-[11px] text-red-600 mt-1">Saldo devedor</div>
              )}
            </div>
            <Button
              size="sm"
              className="bg-[#0A0A0A] hover:bg-black text-white"
              onClick={()=>{ setPixMode("partial"); setPixAmount(debtAmount > 0 ? debtAmount.toFixed(2).replace(".", ",") : ""); setPixOpen(false); setPixData(null); document.getElementById("pix-form")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="public-pay-cta"
            >
              <Wallet size={14} className="mr-1"/> Gerar pagamento
            </Button>
          </div>
        </div>

        {/* Formulário gerar PIX */}
        <div id="pix-form" className="bg-white rounded-md shadow-sm border border-[#E5E5E5] p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Gerar pagamento PIX</div>
          {!data.store?.pix_key && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              A loja ainda não configurou chave PIX. Entre em contato pelo WhatsApp.
            </div>
          )}
          {data.store?.pix_key && (
            <>
              <div>
                <Label className="text-xs">Valor a pagar</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={pixAmount}
                  onChange={(e)=>setPixAmount(e.target.value.replace(/[^\d,.-]/g, ""))}
                  data-testid="public-pix-amount"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={()=>generatePix("partial")}
                  disabled={busy}
                  data-testid="public-generate-partial"
                >
                  Gerar PIX deste valor
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={()=>generatePix("total")}
                  disabled={busy || debtAmount <= 0}
                  data-testid="public-generate-total"
                >
                  Pagar saldo total{debtAmount > 0 ? ` (${brl(debtAmount)})` : ""}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Histórico */}
        <div className="bg-white rounded-md shadow-sm border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Histórico de lançamentos</div>
          {data.entries.length === 0 ? (
            <div className="text-xs text-neutral-500 py-4 text-center border border-dashed border-[#E5E5E5] rounded">Sem lançamentos.</div>
          ) : (
            <div className="space-y-2 divide-y divide-[#F0F0F0]">
              {data.entries.map((e) => (
                <div key={e.id} className="pt-2 first:pt-0" data-testid={`public-entry-${e.id}`}>
                  <div className={`font-display text-base ${e.amount < 0 ? "text-red-600" : "text-emerald-600"}`}>
                    {e.amount < 0 ? "−" : "+"}{brl(Math.abs(e.amount))}
                  </div>
                  <div className="text-xs text-neutral-700 break-words">{stripSupplierTag(e.description)}</div>
                  {e.note && <div className="text-[11px] text-neutral-500 italic mt-0.5">Obs.: {stripSupplierTag(e.note)}</div>}
                  <div className="text-[10px] text-neutral-400 mt-0.5">{fmtDate(e.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[10px] text-neutral-400 text-center pb-4">
          Este link é gerado pela loja. Se você não esperava recebê-lo, ignore esta página.
        </p>
      </div>

      {/* Dialog PIX gerado */}
      <Dialog open={pixOpen} onOpenChange={(o)=>{ if (!o) { setPixOpen(false); setPixData(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pagamento PIX</DialogTitle></DialogHeader>
          {pixData && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Valor</div>
                <div className="font-display text-2xl text-emerald-800">{brl(pixData.amount)}</div>
                {pixData.holder_name && (
                  <div className="text-[11px] text-neutral-600 mt-1">Para: {pixData.holder_name}</div>
                )}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Código PIX (copia e cola)</Label>
                <textarea
                  readOnly
                  value={pixData.brcode}
                  className="w-full mt-1 text-[10px] font-mono p-2 border border-[#E5E5E5] rounded bg-neutral-50 break-all"
                  rows={4}
                  onClick={(e)=>e.target.select()}
                  data-testid="public-pix-brcode"
                />
                <Button
                  className="w-full mt-2 bg-[#0A0A0A] hover:bg-black text-white"
                  size="sm"
                  onClick={copyBrcode}
                  data-testid="public-pix-copy"
                >
                  <Copy size={14} className="mr-1"/> Copiar código
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900">
                <strong>Importante:</strong> Após pagar, envie o comprovante no WhatsApp.
                O sistema NÃO dá baixa automaticamente — a loja precisa confirmar o recebimento manualmente.
              </div>

              <Button
                className="w-full bg-green-500 hover:bg-green-600 text-white"
                onClick={openWhatsApp}
                data-testid="public-pix-send-comprovante"
              >
                <MessageCircle size={14} className="mr-1"/> Mandar Comprovante
              </Button>
              <Button variant="outline" className="w-full" onClick={()=>{ setPixOpen(false); setPixData(null); }}>
                <X size={14} className="mr-1"/> Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
